import { useQuery } from '@tanstack/react-query';
import { supabaseHarvest } from '@/lib/supabase';
import { describeJob } from '@/lib/hangfire-format';
import type { HangfireServerRow } from '@/types/hangfire';

export interface ProcessingJob {
  id: number;
  /** Friendly job class name parsed from invocationdata.Type. */
  jobType: string;
  createdat: string;
  ageSeconds: number;
}

export interface ServerSummary {
  id: string;
  workerCount: number;
  queues: string[];
  lastheartbeat: string;
}

export interface BlockedWorkersResult {
  servers: ServerSummary[];
  processing: ProcessingJob[];
  totalWorkers: number;
  /** Workers currently occupied (processing count, capped at the pool size). */
  busyWorkers: number;
}

function parseInvocation(
  invocationdata: { Type?: string } | string | null,
): string {
  // Enum-dispatched Presto jobs get their target appended (e.g.
  // "PrestoHarvestJob — Event Team Stats"); see lib/hangfire-format.
  return describeJob(invocationdata) ?? 'Unknown job';
}

function readServerData(row: HangfireServerRow): ServerSummary {
  let data: { WorkerCount?: number; Queues?: string[] } | null = null;
  if (typeof row.data === 'string') {
    try {
      data = JSON.parse(row.data) as { WorkerCount?: number; Queues?: string[] };
    } catch {
      data = null;
    }
  } else {
    data = row.data;
  }
  return {
    id: row.id,
    workerCount: Number(data?.WorkerCount) || 0,
    queues: data?.Queues ?? [],
    lastheartbeat: row.lastheartbeat,
  };
}

/**
 * Currently-occupied workers and the jobs blocking them. A Hangfire job in the
 * `Processing` state holds exactly one worker for its entire run, so a small
 * number of long-running Processing jobs can wedge the whole pool (the harvest
 * gridlock). Surfaces each blocker with its running age plus pool utilization.
 */
export function useBlockedWorkers() {
  return useQuery({
    queryKey: ['blocked-workers'],
    refetchInterval: 15_000,
    queryFn: async (): Promise<BlockedWorkersResult> => {
      const [serversRes, processingRes] = await Promise.all([
        supabaseHarvest.from('server').select('id, data, lastheartbeat').order('lastheartbeat', { ascending: false }),
        supabaseHarvest
          .from('job')
          .select('id, invocationdata, createdat')
          .eq('statename', 'Processing')
          .order('createdat', { ascending: true })
          .limit(500),
      ]);
      if (serversRes.error) throw serversRes.error;
      if (processingRes.error) throw processingRes.error;

      const servers = (serversRes.data ?? []).map((r) => readServerData(r as HangfireServerRow));
      const now = Date.now();
      const processing: ProcessingJob[] = (processingRes.data ?? []).map((r) => {
        const row = r as { id: number; invocationdata: { Type?: string } | string | null; createdat: string };
        return {
          id: row.id,
          jobType: parseInvocation(row.invocationdata),
          createdat: row.createdat,
          ageSeconds: Math.max(0, Math.round((now - new Date(row.createdat).getTime()) / 1000)),
        };
      });
      // Oldest (most likely-wedged) first.
      processing.sort((a, b) => b.ageSeconds - a.ageSeconds);

      const totalWorkers = servers.reduce((sum, s) => sum + s.workerCount, 0);
      return {
        servers,
        processing,
        totalWorkers,
        busyWorkers: Math.min(processing.length, totalWorkers || processing.length),
      };
    },
  });
}
